import type { ConnectorCatalogItem } from "@/types/connectors.ts";

/**
 * How a client should initiate this connector's connection.
 *
 * `unsupported` means the catalog item declares a connect kind but not the path
 * it needs — the server has nowhere to send the user, so the client must offer
 * nothing rather than a control that dead-ends.
 */
export type ConnectorConnectMode =
  | "oauth"
  | "apiKey"
  | "credentialJson"
  | "imapCredentials"
  /** The "Custom MCP server" entry tile: the client opens its own add flow. */
  | "customMcp"
  | "unsupported";

export type ConnectorConnectLabel = "Connect" | "Reconnect" | "Add";

export type ConnectorConnectBehavior = {
  mode: ConnectorConnectMode;
  /**
   * Web's button copy. Native draws a bare "+" and ignores it, which is why this
   * module returns a behavior rather than one pre-rendered string.
   */
  label: ConnectorConnectLabel;
  /** Nothing to offer: already connected, healthy, and not repeatable. */
  hidden: boolean;
  /** Connected but credentials expired — the connector stays listed, and reconnects in place. */
  needsReconnect: boolean;
  /** Where the connect goes: `connectPath` for OAuth, `credentialSubmitPath` otherwise. */
  path: string | null;
};

/**
 * The custom-server tile registers a new server rather than connecting an
 * existing one, so its button says what it does.
 */
const labelFor = (mode: ConnectorConnectMode, needsReconnect: boolean): ConnectorConnectLabel => {
  if (mode === "customMcp") return "Add";
  return needsReconnect ? "Reconnect" : "Connect";
};

const modeFor = (item: ConnectorCatalogItem): ConnectorConnectMode => {
  if (item.connectKind === "custom_mcp") return "customMcp";
  if (item.connectKind === "link") return item.connectPath ? "oauth" : "unsupported";
  if (!item.credentialSubmitPath) return "unsupported";
  if (item.connectKind === "imap_credentials") return "imapCredentials";
  return item.connectKind === "api_key" ? "apiKey" : "credentialJson";
};

/**
 * Resolves one catalog item into the single decision every connect surface needs.
 *
 * Centralised because four surfaces — web's Settings list and gallery, and
 * native's gallery and in-thread picker — were each about to branch on
 * `connectKind` plus the connected/needsAuth/repeatable triple independently.
 * They now branch on `mode` instead, and the rules below live in one place.
 */
export const connectorConnectMode = (item: ConnectorCatalogItem): ConnectorConnectBehavior => {
  const needsReconnect = item.connected && item.needsAuth;
  const mode = modeFor(item);
  return {
    mode,
    // A repeatable connector keeps its affordance once connected — a user can
    // hold two Google accounts, and hiding it is what would make "add another
    // calendar" unreachable. Every built-in is repeatable for that reason.
    hidden: item.connected && !item.needsAuth && !item.repeatable,
    needsReconnect,
    label: labelFor(mode, needsReconnect),
    path: mode === "oauth" ? item.connectPath : item.credentialSubmitPath,
  };
};
